import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertCircle,
  Calendar,
  Camera,
  CheckCircle,
  ImageIcon,
  Search,
  Sparkles,
  Trash2,
  Upload,
} from "lucide-react";
import {
  usePedidosLimpezaPaginated,
  useAgendarLimpeza,
  useConcluirLimpeza,
  useReabrirLimpeza,
  useSalvarFotosLimpeza,
  type Pedido,
} from "@/hooks/usePedidos";
import {
  getLimpezaStatusBadgeClass,
  LIMPEZA_STATUS_LABELS,
  type LimpezaStatus,
} from "@/lib/planoLimpeza";
import {
  listagemThLeft,
  listagemThCenter,
  listagemTdLeft,
  listagemTdCenter,
} from "@/components/listagem/listagemTableClasses";
import { cn } from "@/lib/utils";
import { uploadFileToStorage, openStorageUrl } from "@/lib/storageUrl";
import { toast } from "sonner";

function formatDataBr(data?: string | null): string {
  if (!data) return "—";
  const [ano, mes, dia] = data.split("T")[0].split("-");
  if (!ano || !mes || !dia) return data;
  return `${dia}/${mes}/${ano}`;
}

async function uploadFotosLimpeza(
  pedidoId: number,
  files: File[],
  tipo: "antes" | "depois"
): Promise<string[]> {
  const urls: string[] = [];
  for (const file of files) {
    const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
    const path = `limpeza/${pedidoId}/${tipo}/${Date.now()}-${safeName}`;
    const { publicUrl } = await uploadFileToStorage(path, file, {
      buckets: ["uploads", "lovable-uploads"],
    });
    urls.push(publicUrl);
  }
  return urls;
}

function FotosPreview({
  urls,
  onRemove,
  onOpen,
}: {
  urls: string[];
  onRemove?: (index: number) => void;
  onOpen?: (url: string) => void;
}) {
  if (urls.length === 0) {
    return <p className="text-sm text-muted-foreground">Nenhuma foto.</p>;
  }
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
      {urls.map((url, index) => (
        <div key={`${url}-${index}`} className="relative group border rounded-md overflow-hidden bg-gray-50">
          <button
            type="button"
            className="block w-full aspect-square"
            onClick={() => (onOpen ? onOpen(url) : void openStorageUrl(url))}
            title="Abrir foto"
          >
            <img src={url} alt={`Foto ${index + 1}`} className="h-full w-full object-cover" />
          </button>
          {onRemove && (
            <Button
              type="button"
              variant="destructive"
              size="icon"
              className="absolute top-1 right-1 h-7 w-7 opacity-90"
              onClick={() => onRemove(index)}
              title="Remover"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      ))}
    </div>
  );
}

export function Limpeza() {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;
  const [searchTermInput, setSearchTermInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [pedidoAgendar, setPedidoAgendar] = useState<Pedido | null>(null);
  const [dataAgendada, setDataAgendada] = useState("");
  const [obsAgendar, setObsAgendar] = useState("");

  const [pedidoConcluir, setPedidoConcluir] = useState<Pedido | null>(null);
  const [pedidoFotos, setPedidoFotos] = useState<Pedido | null>(null);
  const [fotosAntes, setFotosAntes] = useState<string[]>([]);
  const [fotosDepois, setFotosDepois] = useState<string[]>([]);
  const [novasAntes, setNovasAntes] = useState<File[]>([]);
  const [novasDepois, setNovasDepois] = useState<File[]>([]);
  const [uploadingFotos, setUploadingFotos] = useState(false);

  const agendarLimpeza = useAgendarLimpeza();
  const concluirLimpeza = useConcluirLimpeza();
  const reabrirLimpeza = useReabrirLimpeza();
  const salvarFotosLimpeza = useSalvarFotosLimpeza();

  useEffect(() => {
    if (searchTermInput === "") {
      setSearchTerm("");
      return;
    }
    const t = setTimeout(() => setSearchTerm(searchTermInput), 700);
    return () => clearTimeout(t);
  }, [searchTermInput]);

  const { data: result, isLoading, error } = usePedidosLimpezaPaginated(
    currentPage,
    itemsPerPage,
    searchTerm
  );
  const pedidos = result?.data || [];
  const totalPedidos = result?.count || 0;
  const totalPages = Math.max(1, Math.ceil(totalPedidos / itemsPerPage));

  const resetFotosForm = () => {
    setFotosAntes([]);
    setFotosDepois([]);
    setNovasAntes([]);
    setNovasDepois([]);
  };

  const abrirAgendar = (pedido: Pedido) => {
    setPedidoAgendar(pedido);
    setDataAgendada(pedido.limpeza_data_agendada?.split("T")[0] || "");
    setObsAgendar(pedido.limpeza_observacoes || "");
  };

  const abrirConcluir = (pedido: Pedido) => {
    setPedidoConcluir(pedido);
    setFotosAntes([...(pedido.limpeza_fotos_antes || [])]);
    setFotosDepois([...(pedido.limpeza_fotos_depois || [])]);
    setNovasAntes([]);
    setNovasDepois([]);
  };

  const abrirFotos = (pedido: Pedido) => {
    setPedidoFotos(pedido);
    setFotosAntes([...(pedido.limpeza_fotos_antes || [])]);
    setFotosDepois([...(pedido.limpeza_fotos_depois || [])]);
    setNovasAntes([]);
    setNovasDepois([]);
  };

  const confirmarAgendar = async () => {
    if (!pedidoAgendar || !dataAgendada) {
      toast.error("Informe a data da limpeza.");
      return;
    }
    try {
      await agendarLimpeza.mutateAsync({
        pedidoId: pedidoAgendar.id,
        dataAgendada,
        observacoes: obsAgendar.trim() || null,
      });
      toast.success(`Limpeza agendada para o pedido #${String(pedidoAgendar.id).padStart(6, "0")}`);
      setPedidoAgendar(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao agendar limpeza");
    }
  };

  const confirmarConcluir = async () => {
    if (!pedidoConcluir) return;
    setUploadingFotos(true);
    try {
      let urlsAntes = [...fotosAntes];
      let urlsDepois = [...fotosDepois];
      if (novasAntes.length > 0) {
        urlsAntes = [...urlsAntes, ...(await uploadFotosLimpeza(pedidoConcluir.id, novasAntes, "antes"))];
      }
      if (novasDepois.length > 0) {
        urlsDepois = [...urlsDepois, ...(await uploadFotosLimpeza(pedidoConcluir.id, novasDepois, "depois"))];
      }
      await concluirLimpeza.mutateAsync({
        pedidoId: pedidoConcluir.id,
        fotosAntes: urlsAntes,
        fotosDepois: urlsDepois,
      });
      toast.success(`Limpeza do pedido #${String(pedidoConcluir.id).padStart(6, "0")} marcada como concluída`);
      setPedidoConcluir(null);
      resetFotosForm();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao concluir limpeza");
    } finally {
      setUploadingFotos(false);
    }
  };

  const salvarFotos = async () => {
    if (!pedidoFotos) return;
    setUploadingFotos(true);
    try {
      let urlsAntes = [...fotosAntes];
      let urlsDepois = [...fotosDepois];
      if (novasAntes.length > 0) {
        urlsAntes = [...urlsAntes, ...(await uploadFotosLimpeza(pedidoFotos.id, novasAntes, "antes"))];
      }
      if (novasDepois.length > 0) {
        urlsDepois = [...urlsDepois, ...(await uploadFotosLimpeza(pedidoFotos.id, novasDepois, "depois"))];
      }
      await salvarFotosLimpeza.mutateAsync({
        pedidoId: pedidoFotos.id,
        fotosAntes: urlsAntes,
        fotosDepois: urlsDepois,
      });
      toast.success("Fotos da limpeza salvas");
      setPedidoFotos(null);
      resetFotosForm();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao salvar fotos");
    } finally {
      setUploadingFotos(false);
    }
  };

  const reabrir = async (pedido: Pedido) => {
    try {
      await reabrirLimpeza.mutateAsync(pedido.id);
      toast.success(`Limpeza do pedido #${String(pedido.id).padStart(6, "0")} reaberta`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao reabrir limpeza");
    }
  };

  const renderCamposFotos = (idPrefix: string) => (
    <div className="space-y-5">
      <div className="space-y-2">
        <Label>Fotos da limpeza — Antes</Label>
        <FotosPreview
          urls={fotosAntes}
          onRemove={(index) => setFotosAntes((prev) => prev.filter((_, i) => i !== index))}
        />
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="file"
            multiple
            accept="image/jpeg,image/png,image/webp,image/jpg"
            className="hidden"
            id={`${idPrefix}-antes`}
            onChange={(e) => {
              const files = Array.from(e.target.files || []);
              if (files.length) setNovasAntes((prev) => [...prev, ...files]);
              e.target.value = "";
            }}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => document.getElementById(`${idPrefix}-antes`)?.click()}
          >
            <Upload className="h-4 w-4 mr-1" />
            Adicionar fotos antes
          </Button>
          {novasAntes.length > 0 && (
            <span className="text-xs text-muted-foreground">
              {novasAntes.length} arquivo(s) pendente(s)
            </span>
          )}
        </div>
        {novasAntes.length > 0 && (
          <ul className="text-sm space-y-1">
            {novasAntes.map((file, index) => (
              <li key={`${file.name}-${index}`} className="flex items-center justify-between gap-2">
                <span className="truncate">{file.name}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-red-600"
                  onClick={() => setNovasAntes((prev) => prev.filter((_, i) => i !== index))}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="space-y-2">
        <Label>Fotos da limpeza — Depois</Label>
        <FotosPreview
          urls={fotosDepois}
          onRemove={(index) => setFotosDepois((prev) => prev.filter((_, i) => i !== index))}
        />
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="file"
            multiple
            accept="image/jpeg,image/png,image/webp,image/jpg"
            className="hidden"
            id={`${idPrefix}-depois`}
            onChange={(e) => {
              const files = Array.from(e.target.files || []);
              if (files.length) setNovasDepois((prev) => [...prev, ...files]);
              e.target.value = "";
            }}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => document.getElementById(`${idPrefix}-depois`)?.click()}
          >
            <Upload className="h-4 w-4 mr-1" />
            Adicionar fotos depois
          </Button>
          {novasDepois.length > 0 && (
            <span className="text-xs text-muted-foreground">
              {novasDepois.length} arquivo(s) pendente(s)
            </span>
          )}
        </div>
        {novasDepois.length > 0 && (
          <ul className="text-sm space-y-1">
            {novasDepois.map((file, index) => (
              <li key={`${file.name}-${index}`} className="flex items-center justify-between gap-2">
                <span className="truncate">{file.name}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-red-600"
                  onClick={() => setNovasDepois((prev) => prev.filter((_, i) => i !== index))}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center p-8 text-red-600">
        <AlertCircle className="h-8 w-8 mx-auto mb-2" />
        Erro ao carregar: {error.message}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Limpeza</h2>
        <p className="text-gray-600">
          Pedidos do plano Proteção + Limpeza — agende a data e acompanhe se a limpeza já foi feita.
        </p>
      </div>

      <Card className="border-0 shadow-md">
        <CardHeader>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <CardTitle className="text-gray-900 flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-cyan-600" />
              Pedidos com limpeza ({totalPedidos})
            </CardTitle>
            <div className="relative w-full max-w-sm">
              <Search className="absolute left-2 top-2 h-4 w-4 text-gray-400" />
              <Input
                className="pl-8 h-9"
                placeholder="Buscar cliente ou número..."
                value={searchTermInput}
                onChange={(e) => {
                  setSearchTermInput(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1100px] text-sm">
              <thead>
                <tr className="border-b text-gray-600">
                  <th className={cn(listagemThCenter, "p-3")}>Pedido</th>
                  <th className={cn(listagemThLeft, "p-3")}>Cliente</th>
                  <th className={cn(listagemThCenter, "p-3")}>Cidade/UF</th>
                  <th className={cn(listagemThLeft, "p-3")}>Plano</th>
                  <th className={cn(listagemThCenter, "p-3")}>Data pedido</th>
                  <th className={cn(listagemThCenter, "p-3")}>Data agendada</th>
                  <th className={cn(listagemThCenter, "p-3")}>Status limpeza</th>
                  <th className={cn(listagemThCenter, "p-3")}>Fotos</th>
                  <th className={cn(listagemThCenter, "p-3")}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {pedidos.map((pedido) => {
                  const status = (pedido.limpeza_status || "pendente") as LimpezaStatus;
                  const planoNome =
                    pedido.orcamento?.plano?.nome ||
                    (pedido.orcamento?.plano_id
                      ? `Plano ${pedido.orcamento.plano_id}`
                      : "—");
                  const cidadeUf = [
                    pedido.cliente?.cidade,
                    pedido.cliente?.estado,
                  ]
                    .filter(Boolean)
                    .join(" / ") || "—";
                  const qtdFotos =
                    (pedido.limpeza_fotos_antes?.length || 0) +
                    (pedido.limpeza_fotos_depois?.length || 0);

                  return (
                    <tr key={pedido.id} className="border-b hover:bg-gray-50">
                      <td className={cn(listagemTdCenter, "p-3 font-medium")}>
                        #{String(pedido.id).padStart(6, "0")}
                      </td>
                      <td className={cn(listagemTdLeft, "p-3")}>
                        {pedido.cliente?.nome || `Cliente ${pedido.cliente_id}`}
                      </td>
                      <td className={cn(listagemTdCenter, "p-3")}>{cidadeUf}</td>
                      <td className={cn(listagemTdLeft, "p-3")}>{planoNome}</td>
                      <td className={cn(listagemTdCenter, "p-3")}>
                        {formatDataBr(pedido.data_pedido)}
                      </td>
                      <td className={cn(listagemTdCenter, "p-3")}>
                        {formatDataBr(pedido.limpeza_data_agendada)}
                      </td>
                      <td className={cn(listagemTdCenter, "p-3")}>
                        <div className="flex justify-center">
                          <Badge className={getLimpezaStatusBadgeClass(status)}>
                            {LIMPEZA_STATUS_LABELS[status]}
                          </Badge>
                        </div>
                      </td>
                      <td className={cn(listagemTdCenter, "p-3")}>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => abrirFotos(pedido)}
                          title="Fotos antes e depois"
                        >
                          <Camera className="h-4 w-4 mr-1" />
                          {qtdFotos > 0 ? qtdFotos : "—"}
                        </Button>
                      </td>
                      <td className={cn(listagemTdCenter, "p-3")}>
                        <div className="flex flex-wrap justify-center gap-1">
                          {status !== "concluida" && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => abrirAgendar(pedido)}
                              title="Agendar limpeza"
                            >
                              <Calendar className="h-4 w-4" />
                            </Button>
                          )}
                          {status !== "concluida" && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-green-700 hover:bg-green-50"
                              onClick={() => abrirConcluir(pedido)}
                              disabled={concluirLimpeza.isPending}
                              title="Marcar como feita"
                            >
                              <CheckCircle className="h-4 w-4" />
                            </Button>
                          )}
                          {status === "concluida" && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => void reabrir(pedido)}
                              disabled={reabrirLimpeza.isPending}
                              title="Reabrir (voltar para pendente)"
                            >
                              Reabrir
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {pedidos.length === 0 && (
              <p className="text-center py-8 text-gray-500">
                Nenhum pedido com limpeza inclusa.
              </p>
            )}
            {totalPages > 1 && (
              <div className="flex justify-center gap-2 mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                >
                  Anterior
                </Button>
                <span className="text-sm text-gray-600 self-center">
                  Página {currentPage} de {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage >= totalPages}
                  onClick={() => setCurrentPage((p) => p + 1)}
                >
                  Próxima
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Dialog
        open={!!pedidoAgendar}
        onOpenChange={(open) => {
          if (!open) setPedidoAgendar(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Agendar limpeza — pedido #
              {pedidoAgendar ? String(pedidoAgendar.id).padStart(6, "0") : ""}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              {pedidoAgendar?.cliente?.nome || "Cliente"}
            </p>
            <div>
              <Label htmlFor="data_limpeza">Data da limpeza</Label>
              <Input
                id="data_limpeza"
                type="date"
                value={dataAgendada}
                onChange={(e) => setDataAgendada(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="obs_limpeza">Observações (opcional)</Label>
              <Input
                id="obs_limpeza"
                value={obsAgendar}
                onChange={(e) => setObsAgendar(e.target.value)}
                placeholder="Ex.: manhã, contato no local..."
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPedidoAgendar(null)}>
              Cancelar
            </Button>
            <Button
              onClick={() => void confirmarAgendar()}
              disabled={agendarLimpeza.isPending || !dataAgendada}
              className="bg-cyan-600 hover:bg-cyan-700"
            >
              {agendarLimpeza.isPending ? "Salvando..." : "Agendar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!pedidoConcluir}
        onOpenChange={(open) => {
          if (!open) {
            setPedidoConcluir(null);
            resetFotosForm();
          }
        }}
      >
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ImageIcon className="h-5 w-5 text-cyan-600" />
              Concluir limpeza — pedido #
              {pedidoConcluir ? String(pedidoConcluir.id).padStart(6, "0") : ""}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {pedidoConcluir?.cliente?.nome || "Cliente"} — anexe as fotos antes e depois.
          </p>
          {renderCamposFotos("limpeza-concluir")}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setPedidoConcluir(null);
                resetFotosForm();
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={() => void confirmarConcluir()}
              disabled={uploadingFotos || concluirLimpeza.isPending}
              className="bg-primary hover:bg-primary/90"
            >
              {uploadingFotos || concluirLimpeza.isPending
                ? "Salvando..."
                : "Marcar como concluída"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!pedidoFotos}
        onOpenChange={(open) => {
          if (!open) {
            setPedidoFotos(null);
            resetFotosForm();
          }
        }}
      >
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="h-5 w-5 text-cyan-600" />
              Fotos da limpeza — pedido #
              {pedidoFotos ? String(pedidoFotos.id).padStart(6, "0") : ""}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {pedidoFotos?.cliente?.nome || "Cliente"}
          </p>
          {renderCamposFotos("limpeza-editar")}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setPedidoFotos(null);
                resetFotosForm();
              }}
            >
              Fechar
            </Button>
            <Button
              onClick={() => void salvarFotos()}
              disabled={uploadingFotos || salvarFotosLimpeza.isPending}
              className="bg-cyan-600 hover:bg-cyan-700"
            >
              {uploadingFotos || salvarFotosLimpeza.isPending ? "Salvando..." : "Salvar fotos"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
